import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Building2, Calculator, ExternalLink, LayoutGrid } from "lucide-react";
import OSLayout from "../../components/os/OSLayout";
import { api } from "../../lib/api";
import SelectField from "../../components/os/SelectField";
import { STATUS } from "./GrUnits";

const mxn = (v) => (v == null || v === "" ? "—" : Number(v).toLocaleString("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 0 }));
const CELL = { disponible: "bg-emerald-500 hover:bg-emerald-600 text-white", apartada: "bg-amber-500 hover:bg-amber-600 text-white", vendida: "bg-navy/20 text-navy/50", proxima: "bg-blue-500 hover:bg-blue-600 text-white" };
const typeLabel = { studio: "Studio", "1r": "1 Rec", "2r": "2 Rec", "3r": "3 Rec", ph: "PH" };

export default function GrFloorPlans() {
  const nav = useNavigate();
  const [devs, setDevs] = useState([]);
  const [devId, setDevId] = useState("");
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);

  useEffect(() => { api.get("/gr/developments").then((r) => { setDevs(r.data); if (r.data[0]) setDevId(r.data[0].id); }).finally(() => setLoading(false)); }, []);
  useEffect(() => { if (devId) { setLoading(true); api.get(`/gr/units?development_id=${devId}`).then((r) => setUnits(r.data)).finally(() => setLoading(false)); } }, [devId]);

  // agrupar por torre → piso
  const towers = useMemo(() => {
    const map = {};
    units.forEach((u) => {
      const t = u.tower || "General";
      const f = u.floor != null ? u.floor : 0;
      map[t] = map[t] || {};
      map[t][f] = map[t][f] || [];
      map[t][f].push(u);
    });
    return map;
  }, [units]);
  const devName = devs.find((d) => d.id === devId)?.name || "";

  return (
    <OSLayout title="OMNIA Interactive Floor Plans">
      <div className="space-y-6" data-testid="gr-floorplans-page">
        <div>
          <div className="overline text-navy/40 mb-2 flex items-center gap-2"><Building2 className="w-3.5 h-3.5" /> GOÛT READY™ · Sales Suite</div>
          <h2 className="font-editorial text-4xl tracking-tight">OMNIA Interactive Floor Plans</h2>
          <p className="mt-2 text-navy/50 max-w-2xl">Explora el desarrollo por torres y pisos. Cada unidad es interactiva y muestra su disponibilidad en tiempo real.</p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="min-w-[220px]"><SelectField testid="gr-fp-dev" value={devId || "none"} onChange={(v) => setDevId(v === "none" ? "" : v)} options={devs.length ? devs.map((d) => ({ value: d.id, label: d.name })) : [{ value: "none", label: "Sin desarrollos (créalos en Unit Selector)" }]} /></div>
          <div className="flex flex-wrap gap-3 text-xs text-navy/60">
            {Object.entries(STATUS).map(([k, s]) => <span key={k} className="inline-flex items-center gap-1.5"><span className={`w-3 h-3 rounded ${CELL[k].split(" ")[0]}`} /> {s.label}</span>)}
          </div>
        </div>

        {loading ? <div className="py-12 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div> :
          units.length === 0 ? <div className="card-surface py-12 text-center text-navy/40 text-sm" data-testid="gr-fp-empty">Este desarrollo aún no tiene unidades. Agrégalas en OMNIA Unit Selector.</div> :
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-5" data-testid="gr-fp-grid">
              {Object.entries(towers).map(([tower, floors]) => (
                <div key={tower} className="card-surface p-4">
                  <div className="overline text-navy/40 mb-3 flex items-center gap-1.5"><LayoutGrid className="w-3.5 h-3.5" /> Torre {tower}</div>
                  <div className="space-y-2">
                    {Object.keys(floors).sort((a, b) => Number(b) - Number(a)).map((f) => (
                      <div key={f} className="flex items-center gap-2">
                        <div className="w-14 text-xs text-navy/40 shrink-0">Piso {f}</div>
                        <div className="flex flex-wrap gap-1.5">
                          {floors[f].map((u) => (
                            <button key={u.id} onClick={() => setSel(u)} title={`Unidad ${u.number} · ${STATUS[u.status]?.label}`} data-testid={`gr-cell-${u.id}`}
                              className={`w-14 h-12 rounded-md text-xs font-medium flex flex-col items-center justify-center transition-colors ${CELL[u.status]} ${sel?.id === u.id ? "ring-2 ring-violet ring-offset-1" : ""}`}>
                              <span>{u.number}</span><span className="opacity-80 text-[10px]">{typeLabel[u.type] || ""}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="card-surface p-5 h-fit sticky top-4" data-testid="gr-fp-panel">
              {sel ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between"><div className="font-editorial text-2xl">Unidad {sel.number}</div><span className={`badge ${STATUS[sel.status]?.cls}`}>{STATUS[sel.status]?.label}</span></div>
                  <div className="text-sm text-navy/60">{sel.tower ? `Torre ${sel.tower} · ` : ""}Piso {sel.floor ?? "—"} · {typeLabel[sel.type] || sel.type}</div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                    {[["Área", sel.area ? `${sel.area} m²` : "—"], ["Recámaras", sel.bedrooms ?? "—"], ["Baños", sel.bathrooms ?? "—"], ["Estac.", sel.parking ?? "—"], ["Orientación", sel.orientation || "—"], ["Vista", sel.view || "—"]].map(([k, v]) => (
                      <div key={k} className="flex justify-between border-b border-navy/5 py-1"><span className="text-navy/45">{k}</span><span>{v}</span></div>
                    ))}
                  </div>
                  <div className="font-editorial text-2xl text-violet">{mxn(sel.price)}</div>
                  {sel.promo && <div className="text-xs text-emerald-600">{sel.promo}</div>}
                  <div className="flex flex-wrap gap-2 pt-2">
                    <button onClick={() => nav(`/os/gr/mortgage?price=${sel.price || 0}&property=${encodeURIComponent(`${devName} · ${sel.number}`)}`)} className="btn btn-outline text-sm"><Calculator className="w-3.5 h-3.5" /> Hipoteca</button>
                    {sel.tour_url && <a href={sel.tour_url} target="_blank" rel="noreferrer" className="btn btn-outline text-sm"><ExternalLink className="w-3.5 h-3.5" /> Recorrido 3D</a>}
                    <button onClick={() => nav("/os/gr/units")} className="btn btn-primary text-sm">Ver inventario</button>
                  </div>
                </div>
              ) : <div className="text-sm text-navy/40 text-center py-8">Selecciona una unidad del plano para ver su información.</div>}
            </div>
          </div>}
      </div>
    </OSLayout>
  );
}
